import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Router } from '@angular/router'
import { AuthService } from '../../services/auth.service'
import {
  login,
  loginSuccess,
  loginFailure,
  logout,
  registerFailure,
  registerSuccess,
  register,
  forgotPassword,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordFailure,
  refreshToken,
  refreshTokenSuccess,
  refreshTokenFailure,
  loadCurrentUser,
  loadCurrentUserSuccess,
  loadCurrentUserFailure,
  loadSubscription,
  loadSubscriptionSuccess,
  loadSubscriptionFailure,
  logoutFailure,
  logoutSuccess
} from './auth.actions'
import {
  map,
  mergeMap,
  catchError,
  tap,
  filter,
  take,
  switchMap,
  withLatestFrom,
  exhaustMap
} from 'rxjs/operators'
import { of } from 'rxjs'
import { eps } from '../../services/url.service'
import { ToastrService } from 'ngx-toastr'
import { selectUser } from './auth.selectors'
import { Store } from '@ngrx/store'
import { HttpClient } from '@angular/common/http'
import { DeviceService } from '../../services/device.service'
import { SubscriptionsService, SubscriptionStatus } from '../../services/subscriptions'
import dayjs from 'dayjs'
import { selectSubscription } from './auth.state'

export const loadToken$ = createEffect(
  () => {
    return of(localStorage.getItem('accessToken')).pipe(
      filter((accessToken): accessToken is string => !!accessToken),
      map(accessToken => {
        const userJson = localStorage.getItem('user')
        const user = userJson ? JSON.parse(userJson) : null
        const subscriptionJson = localStorage.getItem('subscription')
        const subscription = subscriptionJson ? JSON.parse(subscriptionJson) : null
        return loginSuccess({
          accessToken,
          user,
          subscription,
          isAuthenticated: true,
          error: null,
          loading: false,
          showThankYouMessage: false,
          refreshToken: localStorage.getItem('accessToken')
        })
      })
    )
  },
  { functional: true }
)

//export const loadToken$ = createEffect(
//  () => {
//    return of(localStorage.getItem('accessToken')).pipe(
//      filter((accessToken): accessToken is string => !!accessToken),
//      mergeMap(accessToken =>
//        validateToken(accessToken).pipe( // validateToken is auth service method that sends the token to the backend for validation and returns the authenticated user if valid
//          map(user => loginSuccess({ accessToken, user })),
//          catchError(() => of(logout()))
//        )
//      )
//    )
//  },
//  { functional: true }
//)

export const auth$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    http = inject(HttpClient),
    deviceService = inject(DeviceService)
  ) =>
    actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) =>
        authService.login(email, password).pipe(
          map(({ accessToken, user, refreshToken, subscription }) => {
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('accessToken', accessToken as string)
            localStorage.setItem('refreshToken', refreshToken as string)
            localStorage.setItem('subscription', JSON.stringify(subscription))
            const isOwner = user?.userRoles?.some((ur: any) => ur.role?.name === 'Business Owner')
            const status = subscription?.subscriptionStatus
            if (status === SubscriptionStatus.PastDue && isOwner) {
              toastr.error(
                'Payment Failed',
                'Your last payment failed. We are retrying the charge, but please update your billing details immediately to avoid service interruption.'
              )
            } else if (
              isOwner &&
              (status === SubscriptionStatus.Expired || status === SubscriptionStatus.Cancelled)
            ) {
              // BLOCKING: Warn user that access is revoked
              toastr.error(
                'Subscription Expired',
                'Your subscription has expired. Access to services is restricted until you renew.'
              )
            } else if (status === SubscriptionStatus.Trialing && isOwner) {
              // EXISTING TRIAL LOGIC
              if (subscription?.expiryDate) {
                const expiry = dayjs(subscription.expiryDate)
                const now = dayjs()

                // Calculate difference in days.
                // e.g., 0.1 days left becomes 1 day. -1 days becomes 0.
                const remainingDays = Math.max(0, Math.ceil(expiry.diff(now, 'day', true)))

                let message = ''
                if (remainingDays > 1) {
                  message = `You have ${remainingDays} days remaining in your trial.`
                  toastr.info('Trial Active', message)
                } else if (remainingDays === 1) {
                  message = `This is the last day of your trial subscription.`
                  toastr.warning('Trial Ending', message)
                } else {
                  message = `Your trial subscription has expired.`
                  toastr.error('Trial Expired', message)
                }
              } else {
                toastr.warning(
                  'Trial Subscription',
                  `You have a trial subscription for ${subscription?.trialDays} days.`
                )
              }
            }

            return loginSuccess({
              accessToken,
              user,
              subscription,
              isAuthenticated: true,
              error: null,
              loading: false,
              showThankYouMessage: false,
              refreshToken
            })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'There was an error during signIn.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(loginFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const redirectAfterLogin$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
    actions$.pipe(
      ofType(loginSuccess),
      withLatestFrom(store.select(selectUser), store.select(selectSubscription)),
      filter(([_, user, __]) => !!user && !!user.employmentType),
      tap(([_, user, subscription]) => {
        if (subscription && subscription.expiryDate) {
          const isExpired = dayjs().isAfter(dayjs(subscription.expiryDate))
          if (isExpired) {
            if (user?.employmentType === 'Business Owner') {
              router.navigate([eps.planAndBilling.upgradePlanPath])
              return
            }
            // For any other role that is NOT admin, log them out.
            if (user?.employmentType !== 'Admin') {
              store.dispatch(logout())
              router.navigate([eps.auth.loginPath])
              return
            }
            // Note: If the role is 'Admin', they will fall through and be handled below.
          }
        }
        if (user?.employmentType === 'Admin') {
          router.navigate([eps.user.dashboardPath])
        } else {
          router.navigate([eps.user.employeeDashboardPath])
        }
      })
    ),
  { functional: true, dispatch: false }
)

// export const removeToken$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), toastr = inject(ToastrService)) =>
//     actions$.pipe(
//       ofType(logout),
//       tap(() => {
//         toastr.clear()
//         localStorage.removeItem('accessToken')
//         localStorage.removeItem('user')
//         router.navigate([eps.auth.loginPath]) // 👈 redirect to login
//       })
//     ),
//   { functional: true, dispatch: false }
// )

// export const removeToken$ = createEffect(
//   (
//     actions$ = inject(Actions),
//     authService = inject(AuthService),
//     toastr = inject(ToastrService),
//     router = inject(Router)
//   ) =>
//     actions$.pipe(
//       ofType(logout),
//       mergeMap(() =>
//         authService.logout().pipe(
//           map(res => {
//             toastr.clear()
//             localStorage.removeItem('accessToken')
//             localStorage.removeItem('user')

//             router.navigate([eps.auth.loginPath])

//             return logoutSuccess({ success: res.success || 'Logged out successfully' })
//           }),
//           catchError(error => {
//             const errorMessage = error?.error?.message || 'Session expired. Please log in again.'

//             toastr.error(errorMessage, 'Session Error')
//             toastr.clear()
//             localStorage.removeItem('accessToken')
//             localStorage.removeItem('user')
//             router.navigate([eps.auth.loginPath])
//             // still force logout on error
//             // localStorage.removeItem('accessToken')
//             // localStorage.removeItem('user')
//             // router.navigate([eps.auth.loginPath])

//             return of(logoutFailure({ error: errorMessage }))
//           })
//         )
//       )
//     ),
//   { functional: true }
// )

export const removeToken$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(logout),
      exhaustMap(() => {
        toastr.clear()
        // localStorage.removeItem('accessToken')
        // localStorage.removeItem('user')
        // router.navigate([eps.auth.loginPath])

        return authService.logout().pipe(
          // map(() => logoutSuccess({ success: 'Logged out' })),
          map(() => {
            // ✅ Clear everything on success
            localStorage.removeItem('accessToken')
            localStorage.removeItem('user')

            router.navigate([eps.auth.loginPath])

            return logoutSuccess({ success: 'Logged out' })
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'Session expired. Please log in again.'

            toastr.error(errorMessage, 'Session Error')
            toastr.clear()
            localStorage.removeItem('accessToken')
            localStorage.removeItem('user')
            router.navigate([eps.auth.loginPath])
            // still force logout on error
            // localStorage.removeItem('accessToken')
            // localStorage.removeItem('user')
            // router.navigate([eps.auth.loginPath])

            return of(logoutFailure({ error: errorMessage }))
          })
        )
      })
    ),
  { functional: true }
)

export const register$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService) // Inject the ToastrService
  ) =>
    actions$.pipe(
      ofType(register),
      exhaustMap(({ firstName, lastName, email, mobile, businessName, planId }) =>
        authService.register({ firstName, lastName, email, mobile, businessName, planId }).pipe(
          map(() => {
            toastr.success('User Registered successfully!', 'Success') // Show success message
            return registerSuccess({ success: '' }) // Dispatch success action
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'There was an error during registration.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(registerFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const forgotPassword$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService) // Inject the ToastrService
  ) =>
    actions$.pipe(
      ofType(forgotPassword), // Action triggered for forgot password
      exhaustMap(({ email }) =>
        authService.forgotPassword({ email }).pipe(
          map(() => {
            toastr.success('Password reset link sent successfully!', 'Success') // Show success message
            return forgotPasswordSuccess({
              message: 'Password reset link sent successfully!'
            }) // Dispatch success action
          }),
          catchError(error => {
            const errorMessage =
              error?.message || 'There was an error during password reset request.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(forgotPasswordFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const resetPassword$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toastr = inject(ToastrService), // Inject the ToastrService
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(resetPassword), // Action triggered for reset password
      exhaustMap(({ token, email, password }) =>
        authService.resetPassword({ token, email, password }).pipe(
          map(() => {
            toastr.success('Password reset successfully!', 'Success') // Show success message
            router.navigate([eps.auth.loginPath])
            return resetPasswordSuccess({
              message: 'Password reset successfully!'
            }) // Dispatch success action
          }),
          catchError(error => {
            const errorMessage = error?.message || 'There was an error during password reset.'
            toastr.error(errorMessage, 'Error') // Show error message
            return of(resetPasswordFailure({ error: errorMessage }))
          })
        )
      )
    ),
  { functional: true }
)

export const refreshToken$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(refreshToken),
      switchMap(() =>
        authService.refreshToken().pipe(
          map(({ accessToken, user }) => {
            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('user', JSON.stringify(user))
            return refreshTokenSuccess({ accessToken, user }) // ✅ user is declared now
          }),
          catchError(error => {
            const errorMessage = error?.error?.message || 'Session expired. Please log in again.'
            toastr.error(errorMessage, 'Session Error')
            return of(refreshTokenFailure())
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadCurrentUser$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService), router = inject(Router)) =>
    actions$.pipe(
      ofType(loadCurrentUser),
      switchMap(() =>
        authService.getCurrentUser().pipe(
          // map(user => loadCurrentUserSuccess({ user })),
          tap(({ accessToken, user, refreshToken }) => {
            // Side effect: save tokens to localStorage
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('accessToken', accessToken ?? '')
            localStorage.setItem('refreshToken', refreshToken ?? '')
          }),
          map(({ accessToken, user, refreshToken }) => {
            if (!user) throw new Error('No user returned')
            const accessTokens = localStorage.getItem('accessToken')
            const refreshTokens = localStorage.getItem('refreshToken')

            if (
              !accessTokens ||
              accessTokens === 'null' ||
              !refreshTokens ||
              refreshTokens === 'null'
            ) {
              router.navigate([eps.auth.loginPath])
            }
            return loadCurrentUserSuccess({ user, accessToken, refreshToken })
          }),
          catchError(err =>
            of(loadCurrentUserFailure({ error: err?.error?.message || 'Failed to fetch user' }))
          )
        )
      )
    ),
  { functional: true }
)

export const loadSubscription$ = createEffect(
  (
    actions$ = inject(Actions),
    subscriptionsService = inject(SubscriptionsService),
    toastr = inject(ToastrService) // Inject the ToastrService
  ) =>
    actions$.pipe(
      ofType(loadSubscription),
      switchMap(action =>
        subscriptionsService.getSubscriptions(action.businessId).pipe(
          map(subscription => loadSubscriptionSuccess({ subscription: subscription[0] })),
          catchError(error => of(loadSubscriptionFailure({ error })))
        )
      )
    ),
  { functional: true }
)

export const authEffects = {
  loadToken$,
  auth$,
  redirectAfterLogin$,
  removeToken$,
  register$,
  forgotPassword$,
  resetPassword$,
  refreshToken$,
  loadCurrentUser$,
  loadSubscription$
}
